import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, CheckCircle2Icon, ShieldIcon, FingerprintIcon, ScanFaceIcon } from 'lucide-react';
import { useTheme } from '../utils/theme';
export type MPINMode = 'enter' | 'set' | 'confirm' | 'success';
export type PinType = 'login' | 'mpin';
interface MPINScreenProps {
  mode: MPINMode;
  pinType?: PinType;
  onComplete: () => void;
  onBack: () => void;
  onModeChange?: (mode: MPINMode) => void;
}
export function MPINScreen({
  mode,
  pinType = 'login',
  onComplete,
  onBack,
  onModeChange
}: MPINScreenProps) {
  const {
    isDark
  } = useTheme();
  const [pin, setPin] = useState('');
  const [firstPin, setFirstPin] = useState('');
  const [error, setError] = useState(false);
  const [biometricPhase, setBiometricPhase] = useState<'idle' | 'scanning' | 'verified'>('idle');
  const [biometricType, setBiometricType] = useState<'face' | 'thumb'>('thumb');

  // ── NEW: Mobile Device Linking modal state ──────────────────────────────
  const [showLinkingModal, setShowLinkingModal] = useState(false);
  const handleBiometricAuth = (type: 'face' | 'thumb') => {
    setBiometricType(type);
    setBiometricPhase('scanning');
    // Simulate scanning
    setTimeout(() => {
      setBiometricPhase('verified');
      // Auto-complete after verified
      setTimeout(() => {
        setBiometricPhase('idle');
        if (mode === 'enter') {
          onComplete();
        }
      }, 800);
    }, 2000);
  };
  const pinLength = pinType === 'mpin' ? 4 : 6;

  // Handle PIN entry
  const handleNumberClick = (num: string) => {
    if (pin.length < pinLength) {
      setPin(prev => prev + num);
      setError(false);
    }
  };
  const handleDelete = () => {
    setPin(prev => prev.slice(0, -1));
    setError(false);
  };

  // Auto-submit when pin reaches required length
  useEffect(() => {
    if (pin.length === pinLength) {
      const timer = setTimeout(() => {
        if (mode === 'enter') {
          onComplete();
        } else if (mode === 'set') {
          setFirstPin(pin);
          setPin('');
          if (onModeChange) onModeChange('confirm');
        } else if (mode === 'confirm') {
          if (pin === firstPin) {
            if (onModeChange) onModeChange('success');
          } else {
            setError(true);
            setPin('');
          }
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [pin, mode, firstPin, onComplete, onModeChange]);

  // ── UPDATED: mode === 'success' only happens after Set + Confirm (i.e. creating a PIN).
  // We no longer auto-complete here — the user must tap "Continue" on the success screen,
  // which opens the Mobile Device Linking modal.
  // (mode === 'enter' calls onComplete() directly elsewhere and never reaches 'success'.)

  const label = pinType === 'mpin' ? 'MPIN' : 'Login PIN';
  const digits = pinType === 'mpin' ? '4' : '6';
  const getTitle = () => {
    switch (mode) {
      case 'enter':
        return `Enter ${label}`;
      case 'set':
        return `Set ${label}`;
      case 'confirm':
        return `Confirm ${label}`;
      case 'success':
        return 'Success!';
    }
  };
  const getSubtitle = () => {
    switch (mode) {
      case 'enter':
        return `Enter your ${digits}-digit ${label} to continue`;
      case 'set':
        return `Create a secure ${digits}-digit ${label}`;
      case 'confirm':
        return `Re-enter your ${label} to confirm`;
      case 'success':
        return `${label} has been set successfully`;
    }
  };
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className={`w-full min-h-screen flex flex-col py-6 px-6 ${isDark ? 'bg-[#1A0E08]' : 'bg-[#FFF5F0]'}`} data-id="element-1549" data-mp="1g1v61hl1bzng">
      {/* Header */}
      <div className="flex items-center justify-between mb-8" data-id="element-1550" data-mp="3ad9vrye1pz37">
        {mode !== 'success' ? <button onClick={onBack} className={`p-2 -ml-2 rounded-full transition-colors ${isDark ? 'hover:bg-white/5' : 'hover:bg-black/5'}`} data-id="element-1551" data-mp="3fdo65n34dl4e">
            <ArrowLeftIcon className={`w-6 h-6 ${isDark ? 'text-white' : 'text-[#1A1A1A]'}`} strokeWidth={2} data-id="element-1552" data-mp="1baw4q6i7eejs" />
          </button> : <div className="w-10" data-id="element-1553" data-mp="16agq7bm6r108" />}
        <img src="https://cdn.magicpatterns.com/uploads/k5mzauqmFki77bNt7CFYh1/Whatsapp_digiBOP-removebg-preview.png" alt="digiBOP Logo" className="h-8 w-auto" data-id="element-1554" data-mp="169ovs44br9bj" />
        <div className="w-10" data-id="element-1555" data-mp="wdlwd60uxosa" />
      </div>

      <AnimatePresence mode="wait" data-id="element-1556" data-mp="wfgekckqv2so">
        {mode === 'success' ? <motion.div key="success" initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="flex-1 flex flex-col items-center justify-center -mt-20" data-id="element-1557" data-mp="rlkkvmbik23b">
            <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          type: 'spring',
          damping: 12,
          stiffness: 200
        }} className="w-24 h-24 rounded-full bg-[#22C55E]/15 flex items-center justify-center mb-6 border-2 border-[#22C55E]/30" data-id="element-1558" data-mp="inie0q53mf84">
              <CheckCircle2Icon className="w-12 h-12 text-[#22C55E]" strokeWidth={1.5} data-id="element-1559" data-mp="3url1gi9azijj" />
            </motion.div>
            <h2 className={`text-[28px] font-bold mb-2 ${isDark ? 'text-white' : 'text-[#1A1A1A]'}`} data-id="element-1560" data-mp="8i0kdkj7xxiw">
              {getTitle()}
            </h2>
            <p className={`text-[15px] ${isDark ? 'text-[#B8A89E]' : 'text-[#4A3B32]'} mb-8`} data-id="element-1561" data-mp="dfseml7c5vq9">
              {getSubtitle()}
            </p>

            {/* Continue button — shown on every success screen; opens device-linking modal */}
            <button onClick={() => setShowLinkingModal(true)} className="w-full max-w-sm active:scale-[0.98] transition-transform" style={{
          height: 56,
          borderRadius: 16,
          background: '#D95A1A',
          color: '#FFFFFF',
          fontSize: 16,
          fontWeight: 700,
          border: 'none',
          cursor: 'pointer',
          boxShadow: '0 8px 20px rgba(217,90,26,0.3)'
        }} data-id="element-1562" data-mp="3gvhb501jvucc">
              Continue
            </button>
          </motion.div> : <motion.div key="input" initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -20
      }} className="flex-1 flex flex-col" data-id="element-1563" data-mp="2q4tnyosajzjo">
            {/* Title */}
            <div className="mb-12 text-center" data-id="element-1564" data-mp="2uwtk0658q8az">
              <div className={`w-12 h-12 rounded-full mx-auto flex items-center justify-center mb-4 ${isDark ? 'bg-[#3D2518]' : 'bg-[#FCECE3]'}`} data-id="element-1565" data-mp="2uy4sukdjm68y">
                <ShieldIcon className="w-6 h-6 text-[#D95A1A]" strokeWidth={2} data-id="element-1566" data-mp="2297h703u77p6" />
              </div>
              <h1 className={`text-[32px] font-bold leading-tight tracking-tight mb-2 ${isDark ? 'text-white' : 'text-[#1A1A1A]'}`} data-id="element-1567" data-mp="2h6hpk0l6nxgo">
                {getTitle()}
              </h1>
              <p className={`text-[15px] ${isDark ? 'text-[#B8A89E]' : 'text-[#4A3B32]'}`} data-id="element-1568" data-mp="3skv76npcfgo9">
                {getSubtitle()}
              </p>
            </div>

            {/* PIN Display */}
            <div className="flex justify-center gap-3 mb-12" data-id="element-1569" data-mp="3v5jgqimiexpe">
              {[...Array(pinLength)].map((_, i) => <motion.div key={i} animate={error ? {
            x: [-5, 5, -5, 5, 0]
          } : {
            scale: pin[i] ? [1, 1.1, 1] : 1
          }} transition={{
            duration: 0.2
          }} className={`w-12 h-14 rounded-xl flex items-center justify-center text-2xl font-bold transition-all ${error ? 'bg-red-50 text-red-500 border-2 border-red-500' : pin[i] ? isDark ? 'bg-[#3D2518] text-white border-2 border-[#D95A1A]' : 'bg-white text-[#1A1A1A] border-2 border-[#D95A1A] shadow-sm' : isDark ? 'bg-[#2D1810] border-2 border-transparent' : 'bg-[#FCECE3] border-2 border-transparent'}`} data-id="element-1570" data-mp="3z8lx483et6i" data-mpi={i}>
                  {pin[i] ? '•' : ''}
                </motion.div>)}
            </div>

            {error && <p className="text-red-500 text-center text-sm font-medium -mt-6 mb-6" data-id="element-1571" data-mp="10prbmd62kc22">
                PINs do not match. Please try again.
              </p>}

            {/* Keypad */}
            <div className="mt-auto grid grid-cols-3 gap-y-6 gap-x-4 max-w-[300px] mx-auto w-full pb-8" data-id="element-1572" data-mp="15i27n4xovdpc">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => <button key={num} onClick={() => handleNumberClick(num.toString())} className={`h-14 rounded-2xl text-[24px] font-medium flex items-center justify-center transition-colors active:scale-95 ${isDark ? 'text-white hover:bg-[#2D1810]' : 'text-[#1A1A1A] hover:bg-white'}`} data-id="element-1573" data-mp="bxtqyrscljgh">
                  {num}
                </button>)}
              <div className="flex items-center justify-center" data-id="element-1574" data-mp="zmd0fan4w2a5">
                {mode === 'enter' && pinType === 'login' && <button onClick={() => handleBiometricAuth('thumb')} className={`h-14 w-14 rounded-2xl flex items-center justify-center transition-colors active:scale-95 ${isDark ? 'text-[#F37024] hover:bg-[#2D1810]' : 'text-[#D95A1A] hover:bg-white'}`} data-id="element-1575" data-mp="uni73erjivbs">
                    <FingerprintIcon className="w-8 h-8" strokeWidth={1.5} data-id="element-1576" data-mp="3aha4fi58v4q4" />
                  </button>}
              </div>
              <button onClick={() => handleNumberClick('0')} className={`h-14 rounded-2xl text-[24px] font-medium flex items-center justify-center transition-colors active:scale-95 ${isDark ? 'text-white hover:bg-[#2D1810]' : 'text-[#1A1A1A] hover:bg-white'}`} data-id="element-1577" data-mp="35g2vhfrd7zhv">
                0
              </button>
              <button onClick={handleDelete} className={`h-14 rounded-2xl flex items-center justify-center transition-colors active:scale-95 ${isDark ? 'text-[#B8A89E] hover:bg-[#2D1810]' : 'text-[#4A3B32] hover:bg-white'}`} data-id="element-1578" data-mp="1uw86y043w0uf">
                <ArrowLeftIcon className="w-7 h-7" strokeWidth={2} data-id="element-1579" data-mp="28oxre03zkxpf" />
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Biometric Overlay */}
      <AnimatePresence data-id="element-1580" data-mp="2dm7qy8l3apqz">
        {biometricPhase !== 'idle' && <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.9
      }} className="fixed inset-0 z-50 flex flex-col items-center justify-center" style={{
        background: isDark ? 'linear-gradient(180deg, rgba(26,14,8,0.95) 0%, rgba(45,24,16,0.95) 100%)' : 'linear-gradient(180deg, rgba(255,247,243,0.95) 0%, rgba(253,238,229,0.95) 100%)',
        backdropFilter: 'blur(10px)'
      }} data-id="element-1581" data-mp="2im2imecovjd3">
            <AnimatePresence mode="wait" data-id="element-1582" data-mp="2p7d9yvd6l3ds">
              {biometricPhase === 'scanning' && <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.9
          }} className="flex flex-col items-center gap-6" data-id="element-1583" data-mp="2tas04euu304y">
                  <motion.div animate={{
              scale: [1, 1.15, 1],
              opacity: [0.7, 1, 0.7]
            }} transition={{
              duration: 1.2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="w-24 h-24 rounded-full flex items-center justify-center" style={{
              background: 'rgba(217, 90, 26, 0.12)',
              border: '2px solid rgba(217, 90, 26, 0.3)'
            }} data-id="element-1584" data-mp="37ywcl13amffa">
                    {biometricType === 'thumb' ? <FingerprintIcon className="w-12 h-12 text-[#D95A1A]" strokeWidth={1.5} data-id="element-1585" data-mp="15cr0ae8qs498" /> : <ScanFaceIcon className="w-12 h-12 text-[#D95A1A]" strokeWidth={1.5} data-id="element-1586" data-mp="15evd5lomcdln" />}
                  </motion.div>
                  <div className="text-center" data-id="element-1587" data-mp="wc2n1lr6a7xe">
                    <p className={`text-[18px] font-bold mb-1 ${isDark ? 'text-white' : 'text-[#1A1A1A]'}`} data-id="element-1588" data-mp="wddy86my6hyj">
                      {biometricType === 'thumb' ? 'Scanning Fingerprint' : 'Scanning Face'}
                    </p>
                    <p className={`text-[13px] ${isDark ? 'text-[#B8A89E]' : 'text-[#4A3B32]'}`} data-id="element-1589" data-mp="1p2up2zux5qsn">
                      {biometricType === 'thumb' ? 'Place your finger on the sensor' : 'Look at the camera'}
                    </p>
                  </div>
                  <button onClick={() => setBiometricPhase('idle')} className={`mt-4 text-[14px] font-bold underline ${isDark ? 'text-[#F37024]' : 'text-[#D95A1A]'}`} data-id="element-1590" data-mp="2cqkq8db3lc22">
                    Cancel
                  </button>
                </motion.div>}

              {biometricPhase === 'verified' && <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.9
          }} className="flex flex-col items-center gap-6" data-id="element-1591" data-mp="2vvh3byeq942a">
                  <motion.div initial={{
              scale: 0.5
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              damping: 12,
              stiffness: 200
            }} className="w-24 h-24 rounded-full bg-[#22C55E]/15 flex items-center justify-center" style={{
              border: '2px solid rgba(34, 197, 94, 0.3)'
            }} data-id="element-1592" data-mp="285d0vud9zacv">
                    <CheckCircle2Icon className="w-12 h-12 text-[#22C55E]" strokeWidth={1.5} data-id="element-1593" data-mp="3ti6byr3xqex4" />
                  </motion.div>
                  <div className="text-center" data-id="element-1594" data-mp="1dvr6cjrhw6f">
                    <p className={`text-[18px] font-bold mb-1 ${isDark ? 'text-white' : 'text-[#1A1A1A]'}`} data-id="element-1595" data-mp="1ckm89ekyidq">
                      Verified Successfully
                    </p>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </motion.div>}
      </AnimatePresence>

      {/* ── NEW: Mobile Device Linking bottom-sheet modal ───────────────── */}
      <AnimatePresence data-id="element-1596" data-mp="1qupsrvkn6fvq">
        {showLinkingModal && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 z-[100]" style={{
          background: 'rgba(0,0,0,0.5)'
        }} data-id="element-1597" data-mp="25j3we8pvu8dc" />

            {/* Bottom sheet */}
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 30,
          stiffness: 300
        }} className="fixed bottom-0 left-0 right-0 z-[101] max-w-[430px] mx-auto" style={{
          background: '#FFFFFF',
          borderTopLeftRadius: 28,
          borderTopRightRadius: 28,
          boxShadow: '0 -10px 40px rgba(0,0,0,0.18)',
          padding: '40px 28px 32px'
        }} data-id="element-1598" data-mp="29ntsnyogdoys">
              {/* Link / chain icon */}
              <div className="flex justify-center mb-6" data-id="element-1599" data-mp="vngwxeluxcxw">
                <svg width="56" height="56" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1600" data-mp="vkuqjiapd3h9">
                  <path d="M9.5 14.5L14.5 9.5" stroke="#9E9E9E" strokeWidth="1.6" strokeLinecap="round" data-id="element-1601" data-mp="3u2q9gj1ab5t1" />
                  <path d="M11 6.5L13 4.5C14.6569 2.84315 17.3431 2.84315 19 4.5C20.6569 6.15685 20.6569 8.84315 19 10.5L17 12.5" stroke="#9E9E9E" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" data-id="element-1602" data-mp="7xcuhakenj8h" />
                  <path d="M13 17.5L11 19.5C9.34315 21.1569 6.65685 21.1569 5 19.5C3.34315 17.8431 3.34315 15.1569 5 13.5L7 11.5" stroke="#9E9E9E" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" data-id="element-1603" data-mp="3becudo45knl5" />
                </svg>
              </div>

              {/* Title */}
              <h3 style={{
            fontSize: 18,
            fontWeight: 700,
            color: '#1A1A1A',
            textAlign: 'center',
            marginBottom: 14
          }} data-id="element-1604" data-mp="38ratnvffgehq">
                Mobile Device Linking
              </h3>

              {/* Description */}
              <p style={{
            fontSize: 14,
            color: '#5A5A5A',
            textAlign: 'center',
            lineHeight: 1.6,
            marginBottom: 32,
            padding: '0 6px'
          }} data-id="element-1605" data-mp="2tw4p7cvzcnqi">
                Your device is being linked to the digiBOP Mobile Banking
                App. Please set up biometric for seamless access in the
                future.
              </p>

              {/* Proceed button */}
              <button onClick={() => {
            setShowLinkingModal(false);
            onComplete();
          }} className="w-full active:scale-[0.98] transition-transform" style={{
            height: 56,
            borderRadius: 14,
            background: '#5A1408',
            color: '#FFFFFF',
            fontSize: 16,
            fontWeight: 600,
            border: 'none',
            cursor: 'pointer'
          }} data-id="element-1606" data-mp="267bn0yorljng">
                Proceed
              </button>
            </motion.div>
          </>}
      </AnimatePresence>
    </motion.div>;
}